// components/PlanUsage.jsx
// Displays plan usage bars in admin dashboard
// Shows jobs, interviews, team members usage with upgrade prompts

import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE || 'https://api.invenire.app';

// Progress bar component
const UsageBar = ({ label, used, max, unit = '', warning = false }) => {
  const percent = max > 0 ? Math.min(100, Math.round((used / max) * 100)) : 0;
  const isLow = percent >= 80;
  const isOut = percent >= 100;
  
  return (
    <div className="usage-item">
      <div className="usage-header">
        <span className="usage-label">{label}</span>
        <span className={`usage-count ${isOut ? 'danger' : isLow ? 'warning' : ''}`}>
          {used}/{max} {unit}
        </span>
      </div>
      <div className="usage-bar-container">
        <div 
          className={`usage-bar-fill ${isOut ? 'danger' : isLow ? 'warning' : ''}`}
          style={{ width: `${percent}%` }}
        />
      </div>
      {warning && isLow && (
        <span className="usage-warning">
          {isOut ? 'Limit reached!' : 'Running low'}
        </span>
      )}
    </div>
  );
};

// Notification alert component
const CapacityAlert = ({ notification, onDismiss, onBuyCredits }) => {
  if (!notification) return null;
  
  const data = notification.data || {};
  
  return (
    <div className="capacity-alert">
      <div className="capacity-alert-icon">⚠️</div>
      <div className="capacity-alert-content">
        <h4>{notification.title}</h4>
        <p>{notification.message}</p>
        <p className="capacity-alert-reset">
          Resets on {new Date(data.reset_date).toLocaleDateString()}
        </p>
      </div>
      <div className="capacity-alert-actions">
        <button className="btn-primary" onClick={onBuyCredits}>
          Buy Credits
        </button>
        <button className="btn-secondary" onClick={onDismiss}>
          Dismiss
        </button>
      </div>
    </div>
  );
};

// Credit purchase modal
const CreditPurchaseModal = ({ isOpen, onClose, tenantId, onSuccess }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const packages = [
    { credits: 10, price: '$60', pricePerCredit: '$6.00' },
    { credits: 25, price: '$125', pricePerCredit: '$5.00', popular: true },
    { credits: 50, price: '$225', pricePerCredit: '$4.50', bestValue: true },
  ];
  
  const handlePurchase = async () => {
    if (!selectedPackage) return;
    
    setLoading(true);
    try {
      // In production, this would create a Stripe checkout session
      // For now, direct purchase (manual/test mode)
      const response = await fetch(`${API_BASE}/plans/credits/purchase`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: tenantId,
          credits: selectedPackage.credits
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        onSuccess(result);
        onClose();
      } else {
        alert('Purchase failed. Please try again.');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Purchase failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content credit-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>×</button>
        
        <h2>Purchase Interview Credits</h2>
        <p className="modal-subtitle">
          Credits never expire and are used after your monthly allocation.
        </p>
        
        <div className="credit-packages">
          {packages.map(pkg => (
            <div 
              key={pkg.credits}
              className={`credit-package ${selectedPackage?.credits === pkg.credits ? 'selected' : ''} ${pkg.popular ? 'popular' : ''} ${pkg.bestValue ? 'best-value' : ''}`}
              onClick={() => setSelectedPackage(pkg)}
            >
              {pkg.popular && <span className="package-badge">Popular</span>}
              {pkg.bestValue && <span className="package-badge best">Best Value</span>}
              <div className="package-credits">{pkg.credits}</div>
              <div className="package-label">interviews</div>
              <div className="package-price">{pkg.price}</div>
              <div className="package-per-credit">{pkg.pricePerCredit}/interview</div>
            </div>
          ))}
        </div>
        
        <button 
          className="btn-primary btn-large"
          onClick={handlePurchase}
          disabled={!selectedPackage || loading}
        >
          {loading ? 'Processing...' : `Purchase ${selectedPackage?.credits || ''} Credits`}
        </button>
      </div>
    </div>
  );
};

// Main PlanUsage component
export default function PlanUsage({ tenantId }) {
  const [usage, setUsage] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Check if any thresholds are met (80% or higher)
  const hasThresholdWarning = (usageData) => {
    if (!usageData) return false;
    
    const jobPercent = usageData.jobs?.max > 0 
      ? (usageData.jobs.used / usageData.jobs.max) * 100 : 0;
    const interviewPercent = usageData.interviews?.total_limit > 0 
      ? (usageData.interviews.used / usageData.interviews.total_limit) * 100 : 0;
    const teamPercent = usageData.team?.max > 0 
      ? (usageData.team.used / usageData.team.max) * 100 : 0;
    
    return jobPercent >= 80 || interviewPercent >= 80 || teamPercent >= 80;
  };
  
  const fetchUsage = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/plans/usage/${tenantId}`);
      if (response.ok) {
        const data = await response.json();
        setUsage(data);
      }
    } catch (error) {
      console.error('Failed to fetch usage:', error);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);
  
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/plans/notifications/${tenantId}?unread_only=true`);
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [tenantId]);

  useEffect(() => {
    if (tenantId) {
      fetchUsage();
      fetchNotifications();
    }
  }, [tenantId, fetchUsage, fetchNotifications]);
  
  // Auto-expand when usage loads if thresholds are met
  useEffect(() => {
    if (usage && hasThresholdWarning(usage)) {
      setIsExpanded(true);
    }
  }, [usage]);
  
  const dismissNotification = async (notificationId) => {
    try {
      await fetch(`${API_BASE}/plans/notifications/${notificationId}/dismiss`, {
        method: 'POST'
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Failed to dismiss notification:', error);
    }
  };
  
  const handleCreditPurchase = (result) => {
    // Refresh usage after purchase
    fetchUsage();
    fetchNotifications();
  };
  
  if (loading) {
    return <div className="plan-usage loading">Loading usage...</div>;
  }
  
  if (!usage) {
    return null;
  }
  
  const capacityNotification = notifications.find(n => n.type === 'capacity_limit_reached');
  const showWarningIndicator = hasThresholdWarning(usage) || capacityNotification;
  
  return (
    <div className="plan-usage">
      <div 
        className="plan-usage-header clickable"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="plan-usage-title">
          <span className={`collapse-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
          <h3>Plan Usage</h3>
          {showWarningIndicator && !isExpanded && (
            <span className="warning-indicator">⚠️</span>
          )}
        </div>
        <span className={`plan-badge ${usage.plan}`}>{usage.plan}</span>
      </div>
      
      {isExpanded && (
        <div className="plan-usage-content">
          {capacityNotification && (
            <CapacityAlert 
              notification={capacityNotification}
              onDismiss={() => dismissNotification(capacityNotification.id)}
              onBuyCredits={() => setShowCreditModal(true)}
            />
          )}
          
          <div className="usage-bars">
            <UsageBar 
              label="Active Jobs"
              used={usage.jobs.used}
              max={usage.jobs.max}
              warning={true}
            />
            
            <UsageBar 
              label="Interviews"
              used={usage.interviews.used}
              max={usage.interviews.total_limit}
              unit={usage.interviews.credits > 0 ? `(+${usage.interviews.credits} credits)` : ''}
              warning={true}
            />
            
            <UsageBar 
              label="Team Members"
              used={usage.team.used}
              max={usage.team.max}
              warning={true}
            />
          </div>
          
          {usage.capacity_queue?.has_waiting && (
            <div className="capacity-queue-info">
              <span className="queue-icon">⏳</span>
              <span>{usage.capacity_queue.waiting} candidate{usage.capacity_queue.waiting !== 1 ? 's' : ''} waiting for capacity</span>
            </div>
          )}
          
          <div className="plan-usage-footer">
            <span className="reset-info">
              Resets in {usage.billing.days_until_reset} days ({new Date(usage.billing.reset_date).toLocaleDateString()})
            </span>
            <button 
              className="btn-link"
              onClick={(e) => { e.stopPropagation(); setShowCreditModal(true); }}
            >
              Buy Credits
            </button>
          </div>
        </div>
      )}
      
      <CreditPurchaseModal
        isOpen={showCreditModal}
        onClose={() => setShowCreditModal(false)}
        tenantId={tenantId}
        onSuccess={handleCreditPurchase}
      />
      
      <style jsx>{`
        .plan-usage {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        
        .plan-usage.loading {
          text-align: center;
          color: #64748b;
          padding: 40px;
        }
        
        .plan-usage-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .plan-usage-header.clickable {
          cursor: pointer;
          user-select: none;
          padding: 4px 0;
          margin: -4px 0;
          border-radius: 8px;
          transition: background-color 0.15s ease;
        }
        
        .plan-usage-header.clickable:hover {
          background-color: rgba(0,0,0,0.03);
        }
        
        .plan-usage-title {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .collapse-icon {
          font-size: 10px;
          color: #64748b;
          transition: transform 0.2s ease;
        }
        
        .collapse-icon.expanded {
          transform: rotate(90deg);
        }
        
        .warning-indicator {
          font-size: 14px;
          animation: pulse 2s infinite;
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        
        .plan-usage-content {
          margin-top: 16px;
          animation: slideDown 0.2s ease;
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .plan-usage-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #0f172a;
        }
        
        .plan-badge {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          text-transform: uppercase;
        }
        
        .plan-badge.starter {
          background: #e0f2fe;
          color: #0284c7;
        }
        
        .plan-badge.growth {
          background: #dcfce7;
          color: #16a34a;
        }
        
        .plan-badge.enterprise {
          background: #f3e8ff;
          color: #9333ea;
        }
        
        .usage-bars {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        
        .usage-item {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        
        .usage-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .usage-label {
          font-size: 13px;
          color: #64748b;
        }
        
        .usage-count {
          font-size: 13px;
          font-weight: 600;
          color: #0f172a;
        }
        
        .usage-count.warning {
          color: #f59e0b;
        }
        
        .usage-count.danger {
          color: #ef4444;
        }
        
        .usage-bar-container {
          height: 8px;
          background: #e2e8f0;
          border-radius: 4px;
          overflow: hidden;
        }
        
        .usage-bar-fill {
          height: 100%;
          background: linear-gradient(135deg, #22d3ee, #0ea5e9);
          border-radius: 4px;
          transition: width 0.3s ease;
        }
        
        .usage-bar-fill.warning {
          background: linear-gradient(135deg, #fbbf24, #f59e0b);
        }
        
        .usage-bar-fill.danger {
          background: linear-gradient(135deg, #f87171, #ef4444);
        }
        
        .usage-warning {
          font-size: 11px;
          color: #f59e0b;
        }
        
        .capacity-alert {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          background: #fef3c7;
          border: 1px solid #f59e0b;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 16px;
        }
        
        .capacity-alert-icon {
          font-size: 24px;
        }
        
        .capacity-alert-content {
          flex: 1;
        }
        
        .capacity-alert-content h4 {
          margin: 0 0 4px 0;
          font-size: 14px;
          font-weight: 600;
          color: #92400e;
        }
        
        .capacity-alert-content p {
          margin: 0;
          font-size: 13px;
          color: #92400e;
        }
        
        .capacity-alert-reset {
          font-size: 12px !important;
          opacity: 0.8;
          margin-top: 4px !important;
        }
        
        .capacity-alert-actions {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        
        .capacity-queue-info {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px;
          background: #fef9c3;
          border-radius: 8px;
          margin-top: 16px;
          font-size: 13px;
          color: #854d0e;
        }
        
        .plan-usage-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid #e2e8f0;
        }
        
        .reset-info {
          font-size: 12px;
          color: #64748b;
        }
        
        .btn-link {
          background: none;
          border: none;
          color: #0ea5e9;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          padding: 0;
        }
        
        .btn-link:hover {
          text-decoration: underline;
        }
        
        .btn-primary {
          background: linear-gradient(135deg, #22d3ee, #0ea5e9);
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
        }
        
        .btn-secondary {
          background: white;
          color: #64748b;
          border: 1px solid #e2e8f0;
          padding: 8px 16px;
          border-radius: 6px;
          font-weight: 500;
          cursor: pointer;
        }
        
        /* Modal styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .modal-content {
          background: white;
          border-radius: 16px;
          padding: 32px;
          max-width: 500px;
          width: 90%;
          position: relative;
        }
        
        .modal-close {
          position: absolute;
          top: 16px;
          right: 16px;
          background: none;
          border: none;
          font-size: 24px;
          color: #64748b;
          cursor: pointer;
        }
        
        .credit-modal h2 {
          margin: 0 0 8px 0;
          font-size: 20px;
          color: #0f172a;
        }
        
        .modal-subtitle {
          margin: 0 0 24px 0;
          color: #64748b;
          font-size: 14px;
        }
        
        .credit-packages {
          display: flex;
          gap: 16px;
          margin-bottom: 24px;
        }
        
        .credit-package {
          flex: 1;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          padding: 20px 16px;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s;
          position: relative;
        }
        
        .credit-package:hover {
          border-color: #22d3ee;
        }
        
        .credit-package.selected {
          border-color: #0ea5e9;
          background: #f0fdfa;
        }
        
        .package-badge {
          position: absolute;
          top: -10px;
          left: 50%;
          transform: translateX(-50%);
          background: #0ea5e9;
          color: white;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 10px;
          font-weight: 600;
        }
        
        .package-badge.best {
          background: #16a34a;
        }
        
        .package-credits {
          font-size: 32px;
          font-weight: 700;
          color: #0f172a;
        }
        
        .package-label {
          font-size: 12px;
          color: #64748b;
          margin-bottom: 8px;
        }
        
        .package-price {
          font-size: 18px;
          font-weight: 600;
          color: #0f172a;
        }
        
        .package-per-credit {
          font-size: 11px;
          color: #64748b;
        }
        
        .btn-large {
          width: 100%;
          padding: 14px 24px;
          font-size: 16px;
        }
        
        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
}
